import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  concat,
  split,
} from '@apollo/client'

import { setContext } from '@apollo/client/link/context'

import { getMainDefinition } from '@apollo/client/utilities'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { DocumentNode } from 'graphql'

import {
  STRAWBERRY_GRAPHQL_URL,
  STRAWBERRY_GRAPHQL_WEBSOCKET_URL,
} from 'shared/config'
import { getAccessTokenId } from 'shared/auth'
import possibleTypesResult from 'graphql-generated-strawberry/possible-types'

// Note: GraphQL server should be located at the same domain as the web app. Otherwise, the authentication system
// will not work properly (we need to send/receive cookies). Currently, browsers are not allowed to send cookies if
// the origin and remote domains are not the same.
const strawberryHttpLink = createHttpLink({
  credentials: 'include',
  uri: STRAWBERRY_GRAPHQL_URL,
  fetch,
})

const authLink = setContext(async (_, { headers }) => {
  const token = await getAccessTokenId()

  if (token) {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
      },
    }
  }
})

const connectionParams = async () => {
  const token = await getAccessTokenId()

  if (token) {
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  }
}

const strawberryWsLink = new GraphQLWsLink(
  createClient({
    url: STRAWBERRY_GRAPHQL_WEBSOCKET_URL,
    connectionParams,
  }),
)

const isSubscription = ({ query }: { query: DocumentNode }) => {
  const definition = getMainDefinition(query)

  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'subscription'
  )
}

// Hasura retired from this split (HASURA_TO_STRAWBERRY_MIGRATION.md, Phase 9 step 2) -- every
// operation is Strawberry-allowlisted (./strawberry-operations.ts) as of 2026-08-10, so the
// per-operation split itself was dead weight. Restore it from git history (see that file's own
// history) if a rollback is ever needed; the old x-hasura-role header link (hasuraRoleLink) is
// gone too -- Strawberry's auth is JWT + permission classes, it never read that header.
const splitLink = split(
  isSubscription,
  strawberryWsLink,
  concat(authLink, strawberryHttpLink),
)

const apolloClient = new ApolloClient({
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
    },
  },
  cache: new InMemoryCache({
    resultCaching: false,
    // Added 2026-08-11 alongside the fragment-consolidation pass -- without this, Apollo
    // Client's default heuristic fragment matcher can't tell that e.g. AnswerSetAttachmentType
    // implements BaseAttachmentInterface, so any fragment spread on an interface type silently
    // resolves to undefined fields even though the network response has them (this applies to
    // shaping the raw response, not just cache reads, so it bit even fetchPolicy: 'no-cache'
    // queries below). See graphql.config.yaml's fragment-matcher plugin comment.
    possibleTypes: possibleTypesResult.possibleTypes,
  }),
  link: splitLink,
})

export default apolloClient
